/**
 * HTTP for the new brand campaign flow (brd_* API) — the web twin of the mobile
 * app's helper/saga_helper.ts.
 *
 * Every call goes through the site's own HttpService, so it sends the same
 * headers (Authorization key + `token`), stores the refreshed token the API
 * returns, and logs out on a 401 — exactly like every other page.
 *
 * The API always answers HTTP 200 and reports the outcome in the body
 * (`status` 1 = success, `info` = message, `data.reason` = a plan limit such as
 * plan_barter_campaign). These helpers always resolve with that body — never
 * throw — so a refusal's reason is never lost (on mobile that was a real bug:
 * genericPostRequest threw the body away and every plan sheet went dead).
 */
import { HttpService } from '../../../core/services/https.service';
import { BASE_URL } from '../../../config/config';

const http = new HttpService();

export type ApiBody<T = any> = {
  status: number;
  info?: string;
  data?: T & { reason?: string };
  [key: string]: any;
};

const failure = (info: string): ApiBody => ({ status: -1, info, data: undefined });

const withQuery = (path: string, params?: Record<string, unknown>) => {
  const url = `${BASE_URL}${path}`;
  if (!params) return url;
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return qs ? `${url}${url.includes('?') ? '&' : '?'}${qs}` : url;
};

/** GET `${BASE_URL}${path}?params` → the API body. */
/**
 * A GET that hasn't answered by then shows as a failure (with Try again)
 * instead of a spinner forever — HttpService has no timeout of its own.
 */
const GET_TIMEOUT_MS = 60000;
const TIMED_OUT = Symbol('timeout');

export async function brdGet<T = any>(path: string, params?: Record<string, unknown>): Promise<ApiBody<T>> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<typeof TIMED_OUT>((resolve) => { timer = setTimeout(() => resolve(TIMED_OUT), GET_TIMEOUT_MS); });
    const body: any = await Promise.race([http.getRequest(withQuery(path, params), null), timeout]);
    if (body === TIMED_OUT) return failure('This is taking too long. Check your connection and try again.');
    if (body && typeof body === 'object' && 'status' in body) return body;
    return failure('Could not reach the server. Check your connection and try again.');
  } catch {
    return failure('Could not reach the server. Check your connection and try again.');
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/** POST JSON → the API body (a refusal included). */
export async function brdPost<T = any>(path: string, payload: unknown): Promise<ApiBody<T>> {
  try {
    const body: any = await http.postRequest(`${BASE_URL}${path}`, payload, null);
    if (body && typeof body === 'object' && 'status' in body) return body;
    return failure('Something went wrong. Please try again.');
  } catch (error: any) {
    const body = error?.data;
    if (body && typeof body === 'object' && 'status' in body) return body;
    return failure('Could not reach the server. Check your connection and try again.');
  }
}

/** POST /upload (multipart) → the API body. */
export async function brdUpload(form: FormData, onProgress?: (percent: number) => void): Promise<ApiBody> {
  try {
    const body: any = await http.postRequest(`${BASE_URL}/upload`, form, 'file', onProgress);
    if (body && typeof body === 'object' && 'status' in body) return body;
    return failure('Upload failed. Please try again.');
  } catch {
    return failure('Could not upload the file. Please try again.');
  }
}

/**
 * The API's `info` as something a person can read. Technical errors ("catch:
 * Error: ER_…") become `fallback`; real validation messages are kept. The
 * original is always logged.
 */
export function friendlyError(info?: string, fallback = 'Something went wrong. Please try again.'): string {
  if (info) console.warn('[brd] API error:', info);
  if (!info || typeof info !== 'string') return fallback;
  const cleaned = info.replace(/^catch\s*:\s*/i, '').replace(/^Error:\s*/i, '').trim();
  const technical = /(bind parameter|ER_|sqlmessage|econnrefused|etimedout|syntax error|cannot read|is not defined|Request Error|stack ==>)/i;
  if (!cleaned || technical.test(cleaned)) return fallback;
  return cleaned;
}

/** A plan-limit reason (plan_*) from a refusal, if any. */
export const reasonOf = (body: ApiBody): string | undefined => {
  const r = body?.data?.reason;
  return typeof r === 'string' && r ? r : undefined;
};
